<template>
  <div class="changelog enhanced">
    <div class="changelog-header">
      <h2 class="changelog-title">Recent Changes</h2>
      <div class="changelog-filters">
        <select
          v-model="filterType"
          class="filter-select"
          @change="handleFilterChange"
          aria-label="Filter by change type"
        >
          <option value="">All Changes</option>
          <option value="Overall Rating Change">Overall Rating</option>
          <option value="Individual Rating Change">Individual Rating</option>
        </select>
      </div>
    </div>
    
    <LoadingSpinner 
      v-if="loading" 
      size="large"
      message="Loading changelog entries..."
      :fullPage="true"
      aria-label="Loading changelog"
    />
    
    <NetworkError
      v-else-if="error && isNetworkError"
      :title="errorTitle"
      :description="error"
      :show-retry="true"
      @retry="handleRetry"
    />
    
    <TimeoutError
      v-else-if="error && errorTitle === 'Request Timeout'"
      :title="errorTitle"
      :description="error"
      :show-retry="true"
      @retry="handleRetry"
    />
    
    <ErrorBoundary 
      v-else-if="error"
      :title="errorTitle"
      :message="error"
      :details="errorDetails"
      :show-retry="true"
      :show-reload="isNetworkError"
      :max-retries="3"
      @retry="handleRetry"
    />
    
    <div v-else class="changelog-entries">
      <div 
        v-for="entry in filteredEntries" 
        :key="entry.id"
        class="changelog-entry"
        :class="`change-type-${getChangeTypeClass(entry.change_type)}`"
      >
        <ChangelogEntry :entry="entry" />
      </div>
      
      <!-- Loading more with pagination loader -->
      <div v-if="loadingMore" class="loading-more-container">
        <PaginationLoader 
          text="Loading more changes..."
          :show-text="true"
          aria-label="Loading more entries"
        />
        <SkeletonPlaceholder 
          v-for="n in 3" 
          :key="`skeleton-${n}`"
          aria-label="Loading placeholder"
        />
      </div>
      
      <!-- Intersection observer target -->
      <div ref="loadMoreTrigger" class="load-more-trigger" v-if="hasNext && !loadingMore"></div>
      
      <!-- End of list indicator -->
      <EndOfListIndicator 
        v-if="!hasNext && changelogs.length > 0"
        title="You've reached the end!"
        :total-items="changelogs.length"
        :show-stats="true"
        :show-back-to-top="true"
        @back-to-top="handleBackToTop"
      />
      
      <div v-if="filteredEntries.length === 0" class="no-entries">
        <div class="no-entries-icon">📄</div>
        <p class="no-entries-text">No changes found for the selected filter.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import apiService from '../../apiService'
import ChangelogEntry from './ChangelogEntry.vue'
import LoadingSpinner from '../ui/LoadingSpinner.vue'
import SkeletonPlaceholder from '../ui/SkeletonPlaceholder.vue'
import PaginationLoader from '../ui/PaginationLoader.vue'
import EndOfListIndicator from '../ui/EndOfListIndicator.vue'
import NetworkError from '../ui/NetworkError.vue'
import TimeoutError from '../ui/TimeoutError.vue'
import ErrorBoundary from '../ui/ErrorBoundary.vue'

export default {
  name: 'EnhancedChangelog',
  components: {
    ChangelogEntry,
    LoadingSpinner,
    SkeletonPlaceholder,
    PaginationLoader,
    EndOfListIndicator,
    NetworkError,
    TimeoutError,
    ErrorBoundary
  },
  setup() {
    // Reactive state
    const changelogs = ref([])
    const loading = ref(true)
    const loadingMore = ref(false)
    const error = ref(null)
    const errorTitle = ref('')
    const errorDetails = ref('')
    const isNetworkError = ref(false)
    const filterType = ref('')
    const hasNext = ref(true)
    const currentPage = ref(1)
    const scrollPosition = ref(0)
    const loadStartTime = ref(0)
    const performanceMetrics = ref({
      initialLoadTime: 0,
      paginationLoadTime: 0,
      totalEntries: 0
    })
    
    // Refs
    const loadMoreTrigger = ref(null)
    
    // Intersection Observer
    let observer = null
    let debounceTimeout = null

    // Computed properties
    const filteredEntries = computed(() => {
      if (!filterType.value) {
        return changelogs.value
      }
      return changelogs.value.filter(entry => entry.change_type === filterType.value)
    })

    // Methods
    const fetchChangelogs = async (page = 1, append = false) => {
      const startTime = performance.now()
      loadStartTime.value = startTime
      
      try {
        if (page === 1) {
          loading.value = true
        } else {
          loadingMore.value = true
        }
        
        // Reset error state
        error.value = null
        errorTitle.value = ''
        errorDetails.value = ''
        isNetworkError.value = false
        
        const filters = filterType.value ? { change_type: filterType.value } : {}
        const response = await apiService.fetchChangelogs(page, 20, filters)
        
        const endTime = performance.now()
        const loadTime = endTime - startTime
        
        if (response && response.data) {
          if (append && page > 1) {
            // Append new entries for infinite scroll
            changelogs.value = [...changelogs.value, ...response.data]
            performanceMetrics.value.paginationLoadTime = loadTime
          } else {
            // Replace entries for initial load or filter changes
            changelogs.value = response.data
            performanceMetrics.value.initialLoadTime = loadTime
          }
          
          hasNext.value = response.hasNext || response.pagination?.hasNext || false
          currentPage.value = page
          performanceMetrics.value.totalEntries = changelogs.value.length
          
          // Performance validation
          if (page === 1 && loadTime > 2000) {
            console.warn(`Initial load took ${Math.round(loadTime)}ms - exceeds 2s target`)
          } else if (page > 1 && loadTime > 1000) {
            console.warn(`Pagination load took ${Math.round(loadTime)}ms - exceeds 1s target`)
          }
          
          console.log(`Changelog ${page === 1 ? 'initial' : 'pagination'} load: ${Math.round(loadTime)}ms`)
        } else {
          // Fallback for old API format
          changelogs.value = response || []
          hasNext.value = false
        }
      } catch (err) {
        console.error('Error fetching changelogs:', err)
        
        // Enhanced error classification
        if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
          errorTitle.value = 'Request Timeout'
          error.value = 'The request timed out. Please check your connection and try again.'
          isNetworkError.value = true
        } else if (err.response?.status >= 500) {
          errorTitle.value = 'Server Error'
          error.value = 'The server encountered an error. Please try again in a moment.'
          errorDetails.value = `HTTP ${err.response.status}: ${err.response.statusText}`
        } else if (err.response?.status === 429) {
          errorTitle.value = 'Too Many Requests'
          error.value = 'You are making requests too quickly. Please wait a moment and try again.'
        } else if (!navigator.onLine) {
          errorTitle.value = 'No Internet Connection'
          error.value = 'Please check your internet connection and try again.'
          isNetworkError.value = true
        } else {
          errorTitle.value = 'Loading Error'
          error.value = 'Failed to fetch changelog entries. Please try again later.'
          errorDetails.value = err.message
        }
      } finally {
        loading.value = false
        loadingMore.value = false
      }
    }

    const loadMoreEntries = async () => {
      if (loadingMore.value || !hasNext.value) return
      
      // Debounce to prevent multiple simultaneous requests
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }
      
      debounceTimeout = setTimeout(async () => {
        try {
          await fetchChangelogs(currentPage.value + 1, true)
        } catch (err) {
          console.error('Error loading more entries:', err)
        }
      }, 100) // 100ms debounce
    }

    const setupIntersectionObserver = () => {
      if (!loadMoreTrigger.value) return
      
      // Detect mobile devices for optimized thresholds
      const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window
      const isSlowConnection = navigator.connection && 
        (navigator.connection.effectiveType === 'slow-2g' || 
         navigator.connection.effectiveType === '2g')
      
      // Adjust thresholds based on device and connection
      const rootMargin = isMobile ? '150px' : '200px' // Smaller margin for mobile
      const threshold = isMobile ? 0.05 : 0.1 // Lower threshold for mobile
      
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (entry.isIntersecting && hasNext.value && !loadingMore.value) {
            // Add extra delay for slow connections
            const delay = isSlowConnection ? 500 : 0
            setTimeout(() => {
              if (hasNext.value && !loadingMore.value) {
                loadMoreEntries()
              }
            }, delay)
          }
        },
        {
          threshold,
          rootMargin,
          // Use passive event listeners for better performance
          passive: true
        }
      )
      
      observer.observe(loadMoreTrigger.value)
    }

    const cleanupObserver = () => {
      if (observer) {
        observer.disconnect()
        observer = null
      }
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
        debounceTimeout = null
      }
    }

    const preserveScrollPosition = () => {
      scrollPosition.value = window.scrollY
    }

    const restoreScrollPosition = async () => {
      await nextTick()
      if (scrollPosition.value > 0) {
        window.scrollTo(0, scrollPosition.value)
      }
    }

    const getChangeTypeClass = (changeType) => {
      return changeType.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
    }

    const handleRetry = async (retryCount) => {
      console.log(`Retrying changelog fetch (attempt ${retryCount})`)
      
      // Reset pagination to start fresh
      currentPage.value = 1
      hasNext.value = true
      changelogs.value = []
      
      // Cleanup and setup observer
      cleanupObserver()
      
      await fetchChangelogs(1, false)
      
      if (!error.value) {
        await nextTick()
        setupIntersectionObserver()
      }
    }

    const handleFilterChange = async () => {
      // Preserve scroll position
      preserveScrollPosition()
      
      // Cleanup observer before filter change
      cleanupObserver()
      
      // Reset pagination state for filter change
      currentPage.value = 1
      hasNext.value = true
      
      // Fetch filtered results
      await fetchChangelogs(1, false)
      
      // Restore scroll position
      await restoreScrollPosition()
      
      // Setup observer again
      await nextTick()
      setupIntersectionObserver()
      
      // Announce filter change to screen readers
      const announcement = filterType.value 
        ? `Filtered to show ${filterType.value} changes`
        : 'Showing all changes'
      
      // Create temporary element for screen reader announcement
      const announcer = document.createElement('div')
      announcer.setAttribute('aria-live', 'polite')
      announcer.setAttribute('aria-atomic', 'true')
      announcer.setAttribute('class', 'sr-only')
      announcer.textContent = announcement
      document.body.appendChild(announcer)
      
      setTimeout(() => {
        document.body.removeChild(announcer)
      }, 1000)
    }

    const handleBackToTop = () => {
      // Scroll to the top of the changelog
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }

    // Lifecycle
    onMounted(async () => {
      await fetchChangelogs(1, false)
      await nextTick()
      setupIntersectionObserver()
    })

    onUnmounted(() => {
      cleanupObserver()
    })

    return {
      changelogs,
      loading,
      loadingMore,
      error,
      errorTitle,
      errorDetails,
      isNetworkError,
      filterType,
      hasNext,
      filteredEntries,
      loadMoreTrigger,
      performanceMetrics,
      getChangeTypeClass,
      handleFilterChange,
      handleRetry,
      handleBackToTop
    }
  }
}
</script>

<style scoped>
@import '../../styles/tier-colors.css';

.changelog.enhanced {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  background: linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%);
  color: #f5f5f5;
  border-radius: 16px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    0 4px 16px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.changelog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
}

.changelog-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, #ff6b6b, #4ecdc4);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.changelog-filters {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-select {
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #f5f5f5;
  font-size: 14px;
  min-width: 160px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.filter-select:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.filter-select:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.2);
  border-color: #4ecdc4;
  box-shadow: 0 0 0 3px rgba(78, 205, 196, 0.3);
}

.filter-select option {
  background: #2a2a2a;
  color: #f5f5f5;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-left-color: #4ecdc4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-spinner.small {
  width: 24px;
  height: 24px;
  border-width: 3px;
  margin-bottom: 8px;
}

.loading-text {
  font-size: 16px;
  color: #bbb;
  margin: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 12px;
  margin: 24px 0;
}

.error-icon {
  font-size: 24px;
  margin-right: 12px;
}

.error-text {
  color: #ff8a80;
  margin: 0;
  font-weight: 500;
}

.changelog-entries {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.changelog-entry {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(5px);
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.changelog-entry::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #4ecdc4, #44a08d);
  transition: width 0.3s ease;
}

.changelog-entry:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.2),
    0 6px 20px rgba(0, 0, 0, 0.1);
}

.changelog-entry:hover::before {
  width: 6px;
}

.change-type-overall-rating-change::before {
  background: linear-gradient(180deg, var(--tier-s-color), var(--tier-a-color));
}

.change-type-individual-rating-change::before {
  background: linear-gradient(180deg, var(--tier-b-color), var(--tier-c-color));
}

.change-type-created::before,
.change-type-album-created::before,
.change-type-session-created::before {
  background: linear-gradient(180deg, #4caf50, #66bb6a);
}

.change-type-updated::before,
.change-type-album-updated::before,
.change-type-session-updated::before {
  background: linear-gradient(180deg, #2196f3, #42a5f5);
}

.change-type-deleted::before,
.change-type-album-deleted::before {
  background: linear-gradient(180deg, #f44336, #ef5350);
}

.change-type-rated::before,
.change-type-rating-added::before {
  background: linear-gradient(180deg, #ffc107, #ffca28);
}

.change-type-tier-changed::before {
  background: linear-gradient(180deg, #9c27b0, #ab47bc);
}

.change-type-rating-removed::before {
  background: linear-gradient(180deg, #795548, #8d6e63);
}

.no-entries {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: #888;
}

.no-entries-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.no-entries-text {
  font-size: 16px;
  margin: 0;
  font-style: italic;
}

.loading-more-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  margin: 16px 0;
  text-align: center;
}

.load-more-trigger {
  height: 1px;
  width: 100%;
  opacity: 0;
  pointer-events: none;
}

.no-more-entries {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  margin: 16px 0;
  text-align: center;
}

.no-more-text {
  font-size: 14px;
  color: #888;
  margin: 0;
  font-style: italic;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Screen reader only class */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Mobile touch optimizations */
@media (max-width: 768px) {
  .changelog.enhanced {
    margin: 0 8px;
    padding: 16px;
    border-radius: 12px;
    /* Enable hardware acceleration for better scroll performance */
    transform: translateZ(0);
    will-change: transform;
  }
  
  .changelog-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    /* Reduce header complexity on mobile */
    margin-bottom: 24px;
  }
  
  .changelog-title {
    font-size: 1.75rem;
    text-align: center;
  }
  
  .filter-select {
    width: 100%;
    /* Larger touch target */
    padding: 12px 16px;
    font-size: 16px; /* Prevent zoom on iOS */
  }
  
  .changelog-entries {
    /* Optimize for touch scrolling */
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }
  
  .changelog-entry {
    padding: 16px;
    /* Reduce shadow complexity on mobile */
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    /* Improve tap performance */
    cursor: pointer;
    -webkit-tap-highlight-color: rgba(78, 205, 196, 0.1);
  }
  
  .changelog-entry:hover {
    /* Reduce expensive transforms on mobile */
    transform: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  /* Optimize loading states for mobile */
  .loading-more-container {
    padding: 16px;
    margin: 12px 0;
  }
  
  .loading-spinner {
    /* Smaller spinner on mobile */
    width: 32px;
    height: 32px;
  }
  
  .loading-spinner.small {
    width: 20px;
    height: 20px;
  }
  
  /* Touch-friendly error boundary */
  .error-boundary {
    padding: 16px;
    margin: 16px 0;
  }
  
  .retry-button,
  .reload-button {
    /* Larger touch targets */
    padding: 14px 24px;
    font-size: 16px;
    min-height: 48px;
    min-width: 120px;
  }
}

/* Very small screens (phones in portrait) */
@media (max-width: 480px) {
  .changelog.enhanced {
    margin: 0 4px;
    padding: 12px;
  }
  
  .changelog-title {
    font-size: 1.5rem;
  }
  
  .changelog-entry {
    padding: 12px;
    gap: 12px;
  }
  
  .changelog-entry::before {
    width: 3px;
  }
  
  .no-more-text {
    font-size: 12px;
    padding: 8px 16px;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .changelog.enhanced {
    border: 2px solid #ffffff;
  }
  
  .changelog-entry {
    border: 2px solid #ffffff;
  }
  
  .filter-select {
    border: 2px solid #ffffff;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .changelog-entry,
  .filter-select,
  .loading-spinner {
    transition: none;
    animation: none;
  }
  
  .changelog-entry:hover {
    transform: none;
  }
}
</style>